<div class="widget-settings d-sm-flex flex-column" [formGroup]="widgetForm">
    <section class="widget-section">
        <div class="install-cards">
            <mat-card class="code-card">
                <div class="code-card__header">
                    <div class="code-card__headline">
                        <mat-card-title>Floating Launcher</mat-card-title>
                        <mat-card-subtitle>Fixed launcher button and modal chat.</mat-card-subtitle>
                    </div>
                    <button (click)="copySnippet(widgetLoaderSnippet, $event, widgetLoaderCopyMessage)" aria-label="Copy widget loader" mat-icon-button
                            type="button">
                        <mat-icon>content_copy</mat-icon>
                    </button>
                </div>
                <pre><code>{{ widgetLoaderSnippet }}</code></pre>
            </mat-card>

            <mat-card class="code-card">
                <div class="code-card__header">
                    <div class="code-card__headline">
                        <mat-card-title>Inline Launcher</mat-card-title>
                        <mat-card-subtitle>Place the launcher button in the page layout and open chat as a modal.</mat-card-subtitle>
                    </div>
                    <button (click)="copySnippet(inlineLauncherSnippet, $event, inlineLauncherCopyMessage)"
                            aria-label="Copy inline launcher" mat-icon-button type="button">
                        <mat-icon>content_copy</mat-icon>
                    </button>
                </div>
                <pre><code>{{ inlineLauncherSnippet }}</code></pre>
            </mat-card>

            <mat-card class="code-card">
                <div class="code-card__header">
                    <div class="code-card__headline">
                        <mat-card-title>Inline Chat</mat-card-title>
                        <mat-card-subtitle>Render the complete chat directly inside the page layout.</mat-card-subtitle>
                    </div>
                    <button (click)="copySnippet(inlineChatSnippet, $event, inlineChatCopyMessage)"
                            aria-label="Copy inline chat" mat-icon-button type="button">
                        <mat-icon>content_copy</mat-icon>
                    </button>
                </div>
                <pre><code>{{ inlineChatSnippet }}</code></pre>
            </mat-card>

            <mat-card class="code-card">
                <div class="code-card__header">
                    <div class="code-card__headline">
                        <mat-card-title>Cookie Configuration</mat-card-title>
                        <mat-card-subtitle>Optional: define cookies to include before loading the script.
                        </mat-card-subtitle>
                    </div>
                    <button (click)="copySnippet(widgetCookieSnippet, $event, widgetCookieCopyMessage)" aria-label="Copy cookie configuration" mat-icon-button
                            type="button">
                        <mat-icon>content_copy</mat-icon>
                    </button>
                </div>
                <pre><code>{{ widgetCookieSnippet }}</code></pre>
            </mat-card>

            <mat-card class="code-card">
                <div class="code-card__header">
                    <div class="code-card__headline">
                        <mat-card-title>Dynamic Context</mat-card-title>
                        <mat-card-subtitle>Optional: inject runtime context used by dynamic widget text generation.</mat-card-subtitle>
                    </div>
                    <button (click)="copySnippet(widgetDynamicContextSnippet, $event, widgetDynamicContextCopyMessage)" aria-label="Copy dynamic widget context"
                            mat-icon-button type="button">
                        <mat-icon>content_copy</mat-icon>
                    </button>
                </div>
                <pre><code>{{ widgetDynamicContextSnippet }}</code></pre>
            </mat-card>
        </div>
        <app-disabled-button-reason [disabled]="testWidgetDisabled"
                                    [reason]="testWidgetDisabledReason">
            <button mat-stroked-button class="outline-button" (click)="injectScript()" [disabled]="testWidgetDisabled">
                <span class="mat-mdc-button-touch-target"></span>
                <span>Test widget in window</span>
            </button>
        </app-disabled-button-reason>
        @if (tested) {
            <small>Attempting to test a second time will cause an error - please refresh to test again</small>
        }
    </section>


    <section class="widget-section widget-section--tight">
        <h3 class="section-heading mat-mdc-card-title">Default Pop-up Text</h3>
        <p class="group-hint">Set the default message shown in the widget bubble. When dynamic widget text is enabled,
            this stays in place until a page-specific variant has been generated or loaded from cache.</p>
        <!-- Text Field -->
        <mat-form-field appearance="fill" class="full-width">
            <mat-label>Text</mat-label>
            <input matInput formControlName="text" placeholder="Enter widget text">
            @if (widgetForm.get('text')?.invalid && widgetForm.get('text')?.touched) {
                <mat-error>
                    Text is required
                </mat-error>
            }
        </mat-form-field>
    </section>

    <section class="widget-section widget-section--tight" [formGroup]="settingsForm">
        <h3 class="section-heading mat-mdc-card-title">Dynamic Pop-up Text</h3>
        <p class="group-hint">
            Generate page-specific launcher messages from the current page and optional runtime context.
        </p>
        <mat-slide-toggle [formControl]="dynamicWidgetTextControl">Enable dynamic pop-up text</mat-slide-toggle>
        @if (dynamicWidgetTextControl.value) {
            <p class="group-hint">
                Generates 4 page-specific variants per normalized URL, caches them, and locks the winner after the
                first 100 recorded popup opens.
            </p>
            <mat-form-field appearance="fill" class="full-width">
                <mat-label>Dynamic pop-up instruction</mat-label>
                <textarea
                    matInput
                    rows="5"
                    [formControl]="dynamicWidgetTextInstructionControl"
                    placeholder="Tone, exclusions, offer priorities, and conversion guidance."></textarea>
                <mat-hint>Used only when dynamic pop-up text is enabled.</mat-hint>
            </mat-form-field>
        }
    </section>

    <section class="widget-section widget-section--tight" [formGroup]="settingsForm">
        <h3 class="section-heading mat-mdc-card-title">Visitor Context</h3>
        <p class="group-hint">
            Values collected by the widget before a chat starts. These are also reflected in the optional install
            snippet above.
        </p>
        <mat-form-field appearance="fill" class="full-width">
            <mat-label>Cookies and localStorage keys</mat-label>
            <mat-chip-grid #contextChipGrid aria-label="Visitor context keys">
                @for (contextKey of (settingsForm.get('cookiesToCollect')?.value || []); track contextKey) {
                    <mat-chip-row (removed)="removeContextKey(contextKey)">
                        {{ contextKey }}
                        <button aria-label="Remove context key" matChipRemove>
                            <mat-icon>cancel</mat-icon>
                        </button>
                    </mat-chip-row>
                }
            </mat-chip-grid>
            <input
                    (matChipInputTokenEnd)="addContextKey($event)"
                    [matChipInputAddOnBlur]="true"
                    [matChipInputFor]="contextChipGrid"
                    [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                    placeholder="hubspotutk, user_vehicles">
            <mat-hint>Collected from cookies first, then localStorage.</mat-hint>
        </mat-form-field>
    </section>

    <section class="widget-section widget-section--tight">
        <h3 class="section-heading mat-mdc-card-title">Pop-up Behavior</h3>
        <p class="group-hint">Allow the teaser bubble to briefly return when someone hovers over the launcher icon after
            dismissing it. Dynamic text experiments reuse the same popup lifecycle.</p>
        <mat-form-field appearance="fill" class="full-width" subscriptSizing="dynamic">
            <mat-label>Widget z-index</mat-label>
            <input matInput [formControl]="zIndexControl" type="number" min="0" max="2147483647" step="1">
            <mat-hint>Controls how the floating launcher stacks against the host site. Legacy default: 3001.</mat-hint>
            @if (zIndexControl.invalid && zIndexControl.touched) {
                <mat-error>Enter a whole number between 0 and 2147483647.</mat-error>
            }
        </mat-form-field>
        <mat-form-field appearance="fill" class="full-width">
            <mat-label>Launcher shape</mat-label>
            <mat-select formControlName="launcherShape">
                @for (option of launcherShapeOptions; track option) {
                    <mat-option [value]="option.key">{{ option.value }}</mat-option>
                }
            </mat-select>
            <mat-hint>Use rectangle for customer-provided non-circular launcher artwork.</mat-hint>
        </mat-form-field>
        <mat-slide-toggle formControlName="popupTailEnabled">
            Show pop-up speech-bubble tail
        </mat-slide-toggle>
        <mat-slide-toggle formControlName="hoverRevealEnabled">
            Enable hover reveal on desktop
        </mat-slide-toggle>
        <mat-form-field appearance="fill" class="full-width">
            <mat-label>Position mode</mat-label>
            <mat-select formControlName="positionMode">
                <mat-option value="fixed">Fixed (default)</mat-option>
                <mat-option value="sticky">Sticky (scroll-safe)</mat-option>
            </mat-select>
            <mat-hint>Switch to sticky when the host site has aggressive scroll handlers.</mat-hint>
        </mat-form-field>
        <div formGroupName="autoClose" class="behavior-panel">
            <mat-slide-toggle formControlName="enabled">
                Automatically close teaser
            </mat-slide-toggle>
            <mat-form-field appearance="fill" class="full-width">
                <mat-label>Open duration (ms)</mat-label>
                <input matInput formControlName="delayMs" min="0" placeholder="2000" type="number">
                <mat-hint>How long the teaser stays open before auto-close triggers.</mat-hint>
            </mat-form-field>
            <div formGroupName="devices" class="scroll-device-toggles">
                <mat-checkbox formControlName="desktop">Desktop</mat-checkbox>
                <mat-checkbox formControlName="tablet">Tablet</mat-checkbox>
                <mat-checkbox formControlName="mobile">Mobile</mat-checkbox>
            </div>
            <mat-checkbox formControlName="closeOnSameClientReferrer">
                Auto-close when navigating inside the same site
            </mat-checkbox>
        </div>
        <mat-slide-toggle formControlName="mobilePopupOnce">
            Show mobile teaser only once across sessions and pages
        </mat-slide-toggle>
        <div formGroupName="scrollReveal">
            <mat-slide-toggle formControlName="enabled">
                Reopen teaser on scroll
            </mat-slide-toggle>
            <mat-form-field appearance="fill" class="full-width">
                <mat-label>Scroll threshold (px)</mat-label>
                <input matInput formControlName="thresholdPx" min="0" placeholder="800" type="number">
                <mat-hint>Trigger when the page scroll position reaches this distance.</mat-hint>
            </mat-form-field>
            <div formGroupName="devices" class="scroll-device-toggles">
                <mat-checkbox formControlName="desktop">Desktop</mat-checkbox>
                <mat-checkbox formControlName="tablet">Tablet</mat-checkbox>
                <mat-checkbox formControlName="mobile">Mobile</mat-checkbox>
            </div>
        </div>
    </section>

    @if (widgetForm.get('styling')) {
        <section class="widget-section" formGroupName="styling">
            <h3 class="section-heading mat-mdc-card-title">Shape &amp; spacing</h3>
            <p class="group-hint">Applies immediately when the widget appears, on desktop and mobile. Leave a field empty to keep the responsive default. All values are in pixels.</p>
            <div class="color-grid">
                @for (field of stylingFields; track field.key) {
                    <mat-form-field appearance="fill" class="full-width" subscriptSizing="dynamic">
                        <mat-label>{{ field.label }}</mat-label>
                        <input matInput type="number" [formControlName]="field.key"
                               [min]="field.min" [max]="field.max" step="1" placeholder="Default">
                        <span matTextSuffix>px</span>
                        <mat-hint>{{ field.hint }}</mat-hint>
                        @if (widgetForm.get('styling.' + field.key)?.invalid) {
                            <mat-error>Enter a whole number from {{ field.min }} to {{ field.max }}, or leave empty.</mat-error>
                        }
                    </mat-form-field>
                }
            </div>
        </section>
    }

    <section class="widget-section">
        <h3 class="section-heading mat-mdc-card-title">Pop-up Images</h3>
        <p class="group-hint">Manage the logos displayed in the widget bubble. When enabled, Widget logo 1 and Widget
            logo 2 alternate in the widget bubble every few seconds.</p>
        <!-- Images Group -->
        <div formGroupName="images" class="d-sm-flex flex-column logos-wrapper">
            <mat-slide-toggle formControlName="logoSwapEnabled">Enable logo swapping</mat-slide-toggle>
            <mat-form-field appearance="fill" class="full-width">
                <mat-label>Launcher logo padding</mat-label>
                <input matInput formControlName="launcherLogoPadding" placeholder="0px">
                <mat-hint>Use 0px for edge-to-edge customer launcher artwork.</mat-hint>
                @if (widgetForm.get('images.launcherLogoPadding')?.invalid && widgetForm.get('images.launcherLogoPadding')?.touched) {
                    <mat-error>Use 0 or 1-4 px, rem, em, or % values.</mat-error>
                }
            </mat-form-field>
            <div class="logo-grid">
                <mat-card class="logo-card">
                    @if (aiLogo1Preview) {
                        <div class="logo-preview">
                            <img [src]="aiLogo1Preview" alt="Widget logo 1 preview">
                        </div>
                    } @else {
                        <div class="logo-placeholder">No image selected</div>
                    }
                    <div class="logo-actions">
                        <button (click)="logo1Input.click()" class="outline-button" mat-stroked-button type="button">
                            Upload image
                        </button>
                        <input #logo1Input (change)="onLogoSelected('aiLogo1', $event)" accept="image/*,.svg" hidden
                               type="file">
                    </div>
                    <mat-form-field appearance="fill" class="full-width">
                        <mat-label>Widget logo 1 URL</mat-label>
                        <input matInput formControlName="aiLogo1" placeholder="https://example.com/logo1.png">
                        <mat-hint>Used when no image is uploaded.</mat-hint>
                        @if (widgetForm.get('images.aiLogo1')?.invalid && widgetForm.get('images.aiLogo1')?.touched) {
                            <mat-error>
                                Enter a valid URL
                            </mat-error>
                        }
                    </mat-form-field>
                </mat-card>

                <mat-card class="logo-card logo-card--secondary" [class.logo-card--disabled]="!isLogoSwapEnabled">
                    @if (aiLogo2Preview) {
                        <div class="logo-preview">
                            <img [src]="aiLogo2Preview" alt="Widget logo 2 preview">
                        </div>
                    } @else {
                        <div class="logo-placeholder">No image selected</div>
                    }
                    <div class="logo-actions">
                        <button (click)="logo2Input.click()" class="outline-button" mat-stroked-button type="button">
                            Upload image
                        </button>
                        <input #logo2Input (change)="onLogoSelected('aiLogo2', $event)" accept="image/*,.svg" hidden
                               type="file">
                    </div>
                    <mat-form-field appearance="fill" class="full-width">
                        <mat-label>Widget logo 2 URL</mat-label>
                        <input matInput formControlName="aiLogo2" placeholder="https://example.com/logo2.png">
                        <mat-hint>Used when swapping between logos.</mat-hint>
                        @if (widgetForm.get('images.aiLogo2')?.invalid && widgetForm.get('images.aiLogo2')?.touched) {
                            <mat-error>
                                Enter a valid URL
                            </mat-error>
                        }
                    </mat-form-field>
                </mat-card>
            </div>
        </div>
    </section>

    <section class="widget-section">
        <!-- Colors Group -->
        <div formGroupName="colors" class="widget-color-settings">
            <div class="color-groups">
                @for (group of colorGroups; track group) {
                    <mat-card>
                        <mat-card-title>{{ group.title }}</mat-card-title>
                        @if (group.description) {
                            <p class="group-hint">{{ group.description }}</p>
                        }
                        <div class="color-grid">
                            @for (control of group.controls; track control) {
                                <div class="color-control">
                                    <app-color-field
                                        [control]="getColorControl(control.key)"
                                        [defaultPreviewColor]="defaultPickerColors[control.key]"
                                        [hint]="control.hint || ''"
                                        [inputId]="control.key + '-color-field'"
                                        [label]="control.label"
                                        [placeholder]="control.supportsTransparent ? 'transparent or #FFFFFF' : '#FFFFFF'"
                                        [supportsTransparent]="control.supportsTransparent === true">
                                    </app-color-field>
                                </div>
                            }
                        </div>
                    </mat-card>
                }
            </div>
        </div>
    </section>
</div>
